import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import SearchBar from '../SearchBar';



const MouseParticleEffect = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const [isActive, setIsActive] = useState(false);

  // Mouse hareketini izleme
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      
      // Sadece her 5 harekette bir yeni parçacık ekle (performans için)
      if (Math.random() > 0.8) {
        createParticle(e.clientX, e.clientY);
      }
      
      // En yakın dişliyi döndür
      setIsActive(true);
    };
    
    // Mouse kalkarsa efekti kapat
    const handleMouseLeave = () => {
      setIsActive(false);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Yeni parçacık oluştur
  const createParticle = (x, y) => {
    const newParticle = {
      id: Date.now() + Math.random(),
      x,
      y,
      size: Math.random() * 6 + 2,
      color: Math.random() > 0.5 ? '#FFFFFF' : '#CCCCCC',
      lifetime: 2000, // msec
      createdAt: Date.now()
    };
    
    setParticles(prev => [...prev, newParticle]);
    
    // Parçacığı belirli bir süre sonra kaldır
    setTimeout(() => {
      setParticles(prev => prev.filter(p => p.id !== newParticle.id));
    }, newParticle.lifetime);
  };

  // Kare uçlu dişli çark SVG oluşturma fonksiyonu
  const createGearSvg = (teethCount = 8) => {
    const radius = 40;
    const innerRadius = 15;
    const toothDepth = 10;
    const toothWidth = Math.PI*(5/4) / teethCount; // Diş genişliği (radian cinsinden)
    
    let path = '';
    for (let i = 0; i < teethCount; i++) {
      const angle = (i / teethCount) * Math.PI * 2;
      const nextAngle = angle + toothWidth;
      const thirdAngle = nextAngle + toothWidth;
      const endAngle = thirdAngle + toothWidth;
      
      // İç çemberdeki başlangıç noktası
      const innerX1 = Math.cos(angle) * radius;
      const innerY1 = Math.sin(angle) * radius;
      
      // Dişin sol köşesi
      const toothLeftX = Math.cos(nextAngle) * radius;
      const toothLeftY = Math.sin(nextAngle) * radius;
      
      // Dişin dış köşesi (sol)
      const toothOuterLeftX = Math.cos(nextAngle) * (radius + toothDepth);
      const toothOuterLeftY = Math.sin(nextAngle) * (radius + toothDepth);
      
      // Dişin dış köşesi (sağ)
      const toothOuterRightX = Math.cos(thirdAngle) * (radius + toothDepth);
      const toothOuterRightY = Math.sin(thirdAngle) * (radius + toothDepth);
      
      // Dişin sağ köşesi
      const toothRightX = Math.cos(thirdAngle) * radius;
      const toothRightY = Math.sin(thirdAngle) * radius;
      
      if (i === 0) {
        path += `M ${innerX1},${innerY1} `;
      }
      
      // Kare uçlu diş oluşturma
      path += `L ${toothLeftX},${toothLeftY} `; // Dişin sol alt köşesi
      path += `L ${toothOuterLeftX},${toothOuterLeftY} `; // Dişin sol üst köşesi
      path += `L ${toothOuterRightX},${toothOuterRightY} `; // Dişin sağ üst köşesi
      path += `L ${toothRightX},${toothRightY} `; // Dişin sağ alt köşesi
    }
    
    // İç daire
    path += `Z M 0,0 m -${innerRadius},0 a ${innerRadius},${innerRadius} 0 1,0 ${innerRadius*2},0 a ${innerRadius},${innerRadius} 0 1,0 -${innerRadius*2},0 Z`;
    
    return path;
  };
  
  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      {/* Mouse takip eden küçük dişli */}
     
      <motion.div 
        className="absolute w-16 h-16 opacity-30"
        animate={{
          x: mousePosition.x - 32,
          y: mousePosition.y - 32,
          rotate: isActive ? 360 : 0,
          scale: isActive ? [1, 1.2, 1] : 1
        }}
        transition={{
          rotate: { duration: 2, ease: "linear", repeat: Infinity },
          scale: { duration: 1, ease: "easeInOut", repeat: Infinity }
        }}
      >
        <svg viewBox="-60 -60 120 120" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" fill="#FFFFFF">
          {/* createGearSvg ile oluşturulan dişli */}
          <path d={createGearSvg(10)} />
        </svg>
      </motion.div>
      
      {/* Parçacıklar */}
      {particles.map(particle => {
        const progress = (Date.now() - particle.createdAt) / particle.lifetime;
        const opacity = 1 - progress;
        
        return (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              left: particle.x,
              top: particle.y,
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              opacity: opacity
            }}
            animate={{
              x: [0, (Math.random() - 0.5) * 100],
              y: [0, (Math.random() - 0.5) * 100],
              opacity: [1, 0]
            }}
            transition={{
              duration: particle.lifetime / 1000,
              ease: "easeOut"
            }}
          />
        );
      })}
      
      {/* Yakın dişlilerin hızlanması efekti için overlay */}
      <div className="absolute inset-0 z-0">
        {[...Array(3)].map((_, index) => {
          const distance = Math.sqrt(
            Math.pow(mousePosition.x - window.innerWidth / 2, 2) + 
            Math.pow(mousePosition.y - window.innerHeight / 2, 2)
          );
          
          const influenceRange = 300; // etki mesafesi
          const effectFactor = Math.max(0, 1 - distance / influenceRange);
          
          return (
            <div 
              key={index}
              className="absolute rounded-full bg-white bg-opacity-5"
              style={{
                left: mousePosition.x - 100,
                top: mousePosition.y - 100,
                width: 200,
                height: 200,
                transform: `scale(${effectFactor})`,
                opacity: effectFactor * 0.3,
                pointerEvents: "none"
              }}
            />
          );
        })}
      </div>
    </div>
  );
};


const Hero = () => {
  const { t } = useTranslation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const heroRef = useRef(null);
  
  // Mouse pozisyonunu takip etme
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
      setIsHovering(true);
    };
    
    const handleMouseLeave = () => {
      setIsHovering(false);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);
    
  // Dişli çarkları ve konumları - Yükseklikleri sınırlandır
  const gears = [
    { size: 180, x: '5%', y: '15%', color: '#333333', speed: 12, floatAmount: 10, floatSpeed: 3.5 },
    { size: 120, x: '22%', y: '12%', color: '#2A2A2A', speed: -15, floatAmount: 12, floatSpeed: 4.0 },
    { size: 200, x: '80%', y: '20%', color: '#3A3A3A', speed: 18, floatAmount: 15, floatSpeed: 4.5 },
    { size: 150, x: '65%', y: '10%', color: '#333333', speed: -14, floatAmount: 11, floatSpeed: 3.8 },
    { size: 140, x: '10%', y: '60%', color: '#3A3A3A', speed: 16, floatAmount: 13, floatSpeed: 4.2 },
    { size: 170, x: '30%', y: '70%', color: '#2A2A2A', speed: -20, floatAmount: 14, floatSpeed: 4.7 },
    { size: 130, x: '55%', y: '85%', color: '#333333', speed: 14, floatAmount: 9, floatSpeed: 3.3 },
    { size: 160, x: '85%', y: '75%', color: '#2A2A2A', speed: -16, floatAmount: 12, floatSpeed: 3.9 },
    { size: 110, x: '45%', y: '30%', color: '#3A3A3A', speed: 22, floatAmount: 10, floatSpeed: 3.6 },
    { size: 190, x: '75%', y: '50%', color: '#2A2A2A', speed: -12, floatAmount: 15, floatSpeed: 4.8 },
    { size: 100, x: '15%', y: '40%', color: '#333333', speed: 10, floatAmount: 8, floatSpeed: 3.1 },
    { size: 90, x: '60%', y: '65%', color: '#3A3A3A', speed: -18, floatAmount: 13, floatSpeed: 4.0 },
    { size: 130, x: '40%', y: '55%', color: '#2A2A2A', speed: 15, floatAmount: 11, floatSpeed: 3.5 },

    // Yeni eklenen dişliler
    { size: 95, x: '8%', y: '85%', color: '#2A2A2A', speed: -11, floatAmount: 9, floatSpeed: 3.3 },
    { size: 115, x: '92%', y: '40%', color: '#333333', speed: 13, floatAmount: 11, floatSpeed: 4.1 },
    { size: 85, x: '25%', y: '45%', color: '#3A3A3A', speed: -17, floatAmount: 13, floatSpeed: 3.6 },
    { size: 125, x: '70%', y: '30%', color: '#2A2A2A', speed: 19, floatAmount: 14, floatSpeed: 5.0 },
    { size: 105, x: '38%', y: '8%', color: '#333333', speed: -13, floatAmount: 10, floatSpeed: 3.4 },
    { size: 100, x: '88%', y: '70%', color: '#3A3A3A', speed: 16, floatAmount: 12, floatSpeed: 4.6 },
    { size: 110, x: '18%', y: '75%', color: '#2A2A2A', speed: -14, floatAmount: 11, floatSpeed: 3.9 },
    { size: 135, x: '78%', y: '5%', color: '#333333', speed: 18, floatAmount: 13, floatSpeed: 4.4 },
    { size: 95, x: '52%', y: '18%', color: '#3A3A3A', speed: -15, floatAmount: 9, floatSpeed: 3.7 },
    { size: 120, x: '3%', y: '35%', color: '#2A2A2A', speed: 17, floatAmount: 14, floatSpeed: 5.1 },

    // Daha fazla dişli ekledim
    { size: 110, x: '50%', y: '90%', color: '#3A3A3A', speed: -16, floatAmount: 12, floatSpeed: 4.0 },
    { size: 130, x: '95%', y: '20%', color: '#2A2A2A', speed: 14, floatAmount: 13, floatSpeed: 4.5 },
    { size: 105, x: '12%', y: '50%', color: '#333333', speed: -12, floatAmount: 11, floatSpeed: 3.8 },
    { size: 125, x: '85%', y: '60%', color: '#2A2A2A', speed: 19, floatAmount: 15, floatSpeed: 4.9 },
    { size: 95, x: '65%', y: '95%', color: '#3A3A3A', speed: -13, floatAmount: 10, floatSpeed: 3.6 },

    // Yeni eklenen 15 dişli
    { size: 140, x: '28%', y: '20%', color: '#2A2A2A', speed: 21, floatAmount: 16, floatSpeed: 5.3 },
    { size: 115, x: '90%', y: '15%', color: '#3A3A3A', speed: -19, floatAmount: 12, floatSpeed: 4.2 },
    { size: 100, x: '6%', y: '78%', color: '#333333', speed: 17, floatAmount: 13, floatSpeed: 4.7 },
    { size: 135, x: '48%', y: '35%', color: '#2A2A2A', speed: -18, floatAmount: 14, floatSpeed: 4.8 },
    { size: 125, x: '82%', y: '42%', color: '#3A3A3A', speed: 22, floatAmount: 15, floatSpeed: 5.0 },
    { size: 110, x: '22%', y: '90%', color: '#2A2A2A', speed: -20, floatAmount: 12, floatSpeed: 4.5 },
    { size: 130, x: '72%', y: '12%', color: '#333333', speed: 18, floatAmount: 13, floatSpeed: 4.6 },
    { size: 145, x: '35%', y: '80%', color: '#3A3A3A', speed: -22, floatAmount: 16, floatSpeed: 5.2 },
    { size: 95, x: '55%', y: '45%', color: '#2A2A2A', speed: 19, floatAmount: 11, floatSpeed: 3.9 },
    { size: 120, x: '15%', y: '25%', color: '#333333', speed: -17, floatAmount: 10, floatSpeed: 3.8 },
    { size: 135, x: '90%', y: '55%', color: '#3A3A3A', speed: 20, floatAmount: 14, floatSpeed: 4.3 },
    { size: 105, x: '68%', y: '75%', color: '#2A2A2A', speed: -16, floatAmount: 12, floatSpeed: 4.1 },
    { size: 125, x: '40%', y: '10%', color: '#333333', speed: 23, floatAmount: 15, floatSpeed: 5.4 },
    { size: 100, x: '92%', y: '80%', color: '#3A3A3A', speed: -15, floatAmount: 13, floatSpeed: 4.7 },
    { size: 110, x: '30%', y: '60%', color: '#2A2A2A', speed: 21, floatAmount: 14, floatSpeed: 4.9 },
];




  // Mouse pozisyonuna göre dişlilerin dönüş hızını hesapla
  const getRotationSpeed = (baseSpeed, gearPosition) => {
    if (!isHovering) return baseSpeed / 3; // Fare ekranda değilken yavaş dönme
    
    // Dişlinin konumu ile fare pozisyonu arasındaki mesafeyi hesapla
    const gearX = parseFloat(gearPosition.x) / 100 * window.innerWidth;
    const gearY = parseFloat(gearPosition.y) / 100 * window.innerHeight;
    
    const distance = Math.sqrt(
      Math.pow(mousePosition.x - gearX, 2) + 
      Math.pow(mousePosition.y - gearY, 2)
    );
    
    // Mesafeye göre hızı ayarla (yakın dişliler daha hızlı döner)
    const maxDistance = Math.sqrt(Math.pow(window.innerWidth, 2) + Math.pow(window.innerHeight, 2)) / 2;
    const speedFactor = 1 - Math.min(distance / maxDistance, 1);
    
    return baseSpeed * (1 + speedFactor * 2);
  };

  // Kare uçlu dişli çark SVG oluşturma fonksiyonu
  const createGearSvg = (teethCount = 8) => {
    const radius = 40;
    const innerRadius = 15;
    const toothDepth = 10;
    const toothWidth = Math.PI*(5/4) / teethCount; // Diş genişliği (radian cinsinden)
    
    let path = '';
    for (let i = 0; i < teethCount; i++) {
      const angle = (i / teethCount) * Math.PI * 2;
      const nextAngle = angle + toothWidth;
      const thirdAngle = nextAngle + toothWidth;
      const endAngle = thirdAngle + toothWidth;
      
      // İç çemberdeki başlangıç noktası
      const innerX1 = Math.cos(angle) * radius;
      const innerY1 = Math.sin(angle) * radius;
      
      // Dişin sol köşesi
      const toothLeftX = Math.cos(nextAngle) * radius;
      const toothLeftY = Math.sin(nextAngle) * radius;
      
      // Dişin dış köşesi (sol)
      const toothOuterLeftX = Math.cos(nextAngle) * (radius + toothDepth);
      const toothOuterLeftY = Math.sin(nextAngle) * (radius + toothDepth);
      
      // Dişin dış köşesi (sağ)
      const toothOuterRightX = Math.cos(thirdAngle) * (radius + toothDepth);
      const toothOuterRightY = Math.sin(thirdAngle) * (radius + toothDepth);
      
      // Dişin sağ köşesi
      const toothRightX = Math.cos(thirdAngle) * radius;
      const toothRightY = Math.sin(thirdAngle) * radius;
      
      if (i === 0) {
        path += `M ${innerX1},${innerY1} `;
      }
      
      // Kare uçlu diş oluşturma
      path += `L ${toothLeftX},${toothLeftY} `; // Dişin sol alt köşesi
      path += `L ${toothOuterLeftX},${toothOuterLeftY} `; // Dişin sol üst köşesi
      path += `L ${toothOuterRightX},${toothOuterRightY} `; // Dişin sağ üst köşesi
      path += `L ${toothRightX},${toothRightY} `; // Dişin sağ alt köşesi
    }
    
    // İç daire
    path += `Z M 0,0 m -${innerRadius},0 a ${innerRadius},${innerRadius} 0 1,0 ${innerRadius*2},0 a ${innerRadius},${innerRadius} 0 1,0 -${innerRadius*2},0 Z`;
    
    return path;
  };

  return (
    <div 
  ref={heroRef} 
  id="hero" 
  className="relative h-[calc(100vh-100px)] flex items-center justify-between px-4 overflow-hidden bg-black"
  style={{ marginBottom: '-2px' }} // Alt kısımda boşluk kalmaması için
>
    
      <div className="absolute inset-0 bg-gradient-to-b from-black to-gray-900 opacity-90 z-0"></div>
      
      {/* Dişli çarklar - hem yatay hem dikey float efekti ile */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {gears.map((gear, index) => (
        <motion.div
          key={index}
          className="absolute"
          style={{
            width: `${gear.size}px`,
            height: `${gear.size}px`,
            left: gear.x,
            top: gear.y,
            // En alttaki dişlileri gizle/sınırla - siluet hizası için
            display: parseFloat(gear.y) > 90 ? 'none' : 'block'
          }}
          animate={{
            rotate: 360 * (gear.speed > 0 ? 1 : -1),
            // Float efekti - hem yatay hem dikey yönde hareket
            x: [
              0, 
              (index % 7 === 0 ? 15 : -15) * Math.random(), // Rastgele yatay hareket
              0, 
              (index % 7 === 0 ? -15 : 15) * Math.random(), // Rastgele karşı yönde hareket
              0
            ],
            y: [
              0, 
              (index % 9 === 0 ? 15 : -15) * Math.random(), // Rastgele dikey hareket
              0, 
              (index % 9 === 0 ? -15 : 15) * Math.random(), // Rastgele karşı yönde hareket
              0
            ]
          }}
          transition={{
            rotate: {
              duration: getRotationSpeed(Math.abs(gear.speed), { x: gear.x, y: gear.y }),
              ease: "linear",
              repeat: Infinity
            },
            x: {
              duration: 5 + index % (7), // Farklı hız için
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "mirror"
            },
            y: {
              duration: 6 + index % (5), // Farklı hız için
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "mirror"
            }
          }}
        >
          <svg
            viewBox="-60 -60 120 120"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
            fill={gear.color}
            opacity={0.25}
          >
            <path d={createGearSvg(index % 3 === 0 ? 8 : (index % 3 === 1 ? 10 : 12))} />
          </svg>
        </motion.div>
      ))}
    </div>
    
    <MouseParticleEffect />

      {/* İçerik - Sola hizalı */}
      <motion.div
        className="relative z-10 text-left max-w-xl ml-10 md:ml-20 lg:ml-32"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.h1
          className="text-5xl md:text-7xl font-bold mb-6 font-poppins"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <span className="text-white">{t('hero.titleStart')}</span><br />
          <span className="text-white">{t('hero.titleMiddle')}</span><br />
          <span className="text-cyan-400">{t('hero.titleHighlight')}</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <SearchBar />
        </motion.div>
        
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-10 font-roboto max-w-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          {t('hero.subtitle')}
        </motion.p>
        
        <motion.div
          className="flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <a
            href="#contact"
            className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-md transition-colors duration-300 transform hover:scale-105 inline-block"
          >
            {t('hero.demoButton')}
          </a>
          
        </motion.div>
      </motion.div>
      
      <motion.div 
  className="relative z-10 hidden md:block w-1/3 h-full max-w-md mr-10 md:mr-20 lg:mr-32"
  initial={{ opacity: 0, x: 200 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ 
    delay: 0.8, 
    duration: 1.2,
    type: "spring", 
    stiffness: 50
  }}
>
  <motion.img 
    src="/ajan-siluet.png" 
    alt="Agent Silhouette" 
    className="object-contain h-full filter invert brightness-100"
    initial={{ 
      scale: 1.95,
      y: '5%',
    }}
    animate={{
      scale: 1.95, // Scale'i animate içinde de belirtmek önemli
      y: '5%',
      x: [0, 500, 0], // Sağa kayıp geri dönme
      rotate: [0, 0, -50, 0], // -50 derece dönüp eski haline dönme
    }}
    transition={{
      scale: { duration: 15 }, 
      y: { duration: 15 }, 
      x: { 
        duration: 20, 
        times: [0, 0.8, 1], 
        repeat: Infinity, 
        repeatDelay: 30, // Her tekrardan sonra 10 saniye bekle
        ease: "easeInOut" 
      },
      rotate: { 
        duration: 20, 
        times: [0, 0.8, 1], 
        repeat: Infinity, 
        repeatDelay: 30, // Her tekrardan sonra 10 saniye bekle
        ease: "easeInOut" 
      }
    }}
    style={{
      marginBottom: '-240px',
      transformOrigin: 'center center'
    }}
  />
</motion.div>
      

<div className="absolute bottom-0 w-full" style={{ marginBottom: "0" }}>
  <div className="container mx-auto px-4">
    <motion.div 
      className="flex justify-center items-end h-52 max-w-5xl mx-auto relative"
      style={{ marginRight: "auto", marginLeft: "0", paddingLeft: "5%" }} // Sola kaydırma
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.2, duration: 0.8 }}
    >
      {/* Siyah-beyaz tonlarda çubuklar */}
      {[...Array(8)].map((_, index) => {
        // Daha dramatik parabolik artış fonksiyonu
        const parabolikYukseklik = 20 + (8 * Math.pow(index, 2.2));
        
        return (
          <motion.div
            key={index}
            className={`mx-1 md:mx-2 rounded-t-md bg-opacity-90`}
            style={{ 
              width: `${40 + (index * 8)}px`, // Daha geniş çubuklar
              backgroundColor: index % 3 === 0 ? 'rgba(255, 255, 255, 0.8)' : 
                              index % 3 === 1 ? 'rgba(200, 200, 200, 0.7)' : 
                                              'rgba(150, 150, 150, 0.9)', // Siyah-beyaz tonları
              backdropFilter: 'blur(4px)',
              boxShadow: '0 0 15px rgba(255, 255, 255, 0.2)',
              marginBottom: "0" // Kesinlikle en alta bitişik
            }}
            initial={{ height: 0 }}
            animate={{ height: [0, parabolikYukseklik] }}
            transition={{ 
              delay: 1.2 + (index * 0.15),
              duration: 1.6,
              ease: "easeOut"
            }}
          />
        );
      })}
      
      {/* Daha fazla yükselen ok */}
      {[...Array(5)].map((_, index) => (
        <motion.div
          key={`arrow-${index}`}
          className="absolute"
          style={{ 
            left: `${15 + (index * 18)}%`,
            bottom: index % 2 === 0 ? '40px' : '60px' // Farklı yüksekliklerde oklar
          }}
          initial={{ y: 50, opacity: 0 }}
          animate={{ 
            y: [50, -100],
            opacity: [0, 1, 0]
          }}
          transition={{ 
            delay: 1.8 + (index * 0.4),
            duration: 2.2,
            repeat: Infinity,
            repeatDelay: index * 0.7
          }}
        >
          <svg width="45" height="45" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 3L20 13H4L12 3Z" fill="#FFFFFF" /> {/* Beyaz oklar */}
          </svg>
        </motion.div>
      ))}
      
      {/* Rastgele yüzde değerleri */}
      {[...Array(4)].map((_, index) => {
        // Her animasyon döngüsünde rastgele değerler oluştur
        return (
          <motion.div
            key={`stat-${index}`}
            className="absolute text-white font-bold text-3xl md:text-5xl" // Beyaz metin
            style={{ 
              left: `${10 + (index * 25)}%`,
              bottom: '70px',
              textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
            }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ 
              y: [20, -70, -40],
              opacity: [0, 1, 0]
            }}
            transition={{ 
              delay: 2 + (index * 0.9),
              duration: 2.8,
              repeat: Infinity,
              repeatDelay: index * 0.7
            }}
          >
            {/* Her render'da rastgele bir yüzde değeri */}
            +{Math.floor(Math.random() * 160 + 20)}%
          </motion.div>
        );
      })}
    </motion.div>
  </div>
</div>


      {/* Aşağı Kaydır İkonu */}
      <motion.div
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </motion.div>
      </motion.div>
      
      {/* Bir sonraki bölüme geçiş için kesin bitirme çizgisi */}
      <div className="absolute bottom-0 w-full h-1 bg-black z-20"></div>
    </div>
  );
};

export default Hero;